import React, { createContext, useContext, useState, ReactNode } from "react";

export type MaterialViewLevel =
  | "ORDER"
  | "PLANTS"
  | "MANUAL"
  | "MATERIALS"
  | "DEPONIE_ARTICLES"
  | "DEPONIE_PLANTS";

interface AllowScrollContextType {
  allowScroll: boolean;
  setAllowScroll: React.Dispatch<React.SetStateAction<boolean>>;
  view: MaterialViewLevel;
  setView: React.Dispatch<React.SetStateAction<MaterialViewLevel>>;
  selectedOrderId: number | null;
  setSelectedOrderId: React.Dispatch<React.SetStateAction<number | null>>;
  selectedPlantKey: string | null;
  setSelectedPlantKey: React.Dispatch<React.SetStateAction<string | null>>;
  autoPlantSelected: boolean;
  setAutoPlantSelected: React.Dispatch<React.SetStateAction<boolean>>;
}

// Create the context with a default value
const AllowScrollContext = createContext<AllowScrollContextType | undefined>(undefined);

// Create a provider component
export const AllowScrollProvider = ({ children }: { children: ReactNode }) => {
  const [allowScroll, setAllowScroll] = useState(false);
  const [view, setView] = useState<MaterialViewLevel>("ORDER");
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [selectedPlantKey, setSelectedPlantKey] = useState<string | null>(null);
  const [autoPlantSelected, setAutoPlantSelected] = useState(false);

  return (
    <AllowScrollContext.Provider
      value={{
        allowScroll,
        setAllowScroll,
        view,
        setView,
        selectedOrderId,
        setSelectedOrderId,
        selectedPlantKey,
        setSelectedPlantKey,
        autoPlantSelected,
        setAutoPlantSelected,
      }}
    >
      {children}
    </AllowScrollContext.Provider>
  );
};

// Create a custom hook to consume the context
export const useAllowScroll = (): AllowScrollContextType => {
  const context = useContext(AllowScrollContext);
  if (!context) {
    throw new Error("useAllowScroll must be used within an AllowScrollProvider");
  }
  return context;
};
